import React from 'react';
import { cn } from './cn';
import { useKeyboardOpen } from '@/hooks/useKeyboardOpen';

export interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Render as the primary (brand-filled) action. */
  primary?: boolean;
}

export interface QuickActionBarProps {
  actions: QuickAction[];
  /** Optional leading context label ("Queen Bedroom › Ceiling fan"). */
  context?: React.ReactNode;
  /**
   * Fix to bottom of viewport as a floating bar. Defaults to true — mirrors
   * the inspector-mobile kit spec: detached from the edge, rounded, shadowed,
   * with safe-area padding for the iOS home indicator.
   */
  sticky?: boolean;
  className?: string;
}

export const QuickActionBar: React.FC<QuickActionBarProps> = ({
  actions, context, sticky = true, className,
}) => {
  const keyboardOpen = useKeyboardOpen();
  return (
  <div
    className={cn(
      // Desktop: offset the left edge by the sidebar width (232px, see
      // .iw-sidebar in template-system.css) so the bar's horizontal range
      // matches the main content column. The inner `mx-auto max-w-2xl` then
      // centers within that range — same centering rule as `.iw-main` — and
      // the bar lines up with the content it controls instead of the viewport.
      sticky && 'fixed left-3 right-3 md:left-[232px] md:right-0 z-30',
      // Mobile: MobileTabBar is 56px tall and fixed at bottom. Lift the
      // action bar above it (+12px gap, +safe-area for the iOS home indicator).
      // Desktop: just breathe from the bottom edge.
      sticky && 'bottom-[calc(56px+12px+env(safe-area-inset-bottom))] md:bottom-4',
      sticky && 'md:pb-[env(safe-area-inset-bottom)]',
      // Slide off-screen with the MobileTabBar while the keyboard is open
      // so the inspector gets the full visible viewport for the field
      // they're filling in.
      sticky && 'transition-transform duration-200',
      sticky && keyboardOpen && 'translate-y-[120%]',
      'pointer-events-none',
      className,
    )}
    aria-hidden={keyboardOpen}
  >
    <div className="pointer-events-auto mx-auto max-w-2xl">
      {context && (
        <div className="mb-1.5 px-1">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-caption font-medium text-white/95 bg-ink/70 backdrop-blur-sm shadow truncate max-w-full">
            {context}
          </span>
        </div>
      )}
      <div
        className={cn(
          'flex items-stretch gap-2 p-2 rounded-2xl',
          'bg-surface-1 border border-border-subtle shadow-lg',
        )}
      >
        {actions.map(a => (
          <button
            key={a.id}
            type="button"
            onClick={a.onClick}
            disabled={a.disabled}
            className={cn(
              'flex-1 inline-flex items-center justify-center gap-1.5',
              'rounded-xl px-3 py-2.5 text-body-sm font-medium',
              'transition-colors duration-180',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
              a.disabled && 'opacity-40 cursor-not-allowed',
              a.primary
                ? 'bg-brand text-ink-inverse border border-brand hover:brightness-95 active:brightness-90'
                : 'bg-surface-1 text-ink border border-border-subtle hover:bg-surface-2 active:bg-surface-2',
            )}
            aria-label={a.label}
          >
            {/* iOS Safari collapses inline SVGs without explicit width/height
                to 0×0 inside flex containers. Force the child SVG to fill
                this fixed-size span so the icons render on iPhone. */}
            <span className="w-5 h-5 flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full">{a.icon}</span>
            <span>{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  </div>
  );
};
